// core/interceptors/auth.interceptor.ts
//
// On a 401 (expired access token) this interceptor transparently refreshes the
// access token via the rotating refresh token and retries the failed request,
// queueing any concurrent requests until the refresh resolves. If the refresh
// fails (no/expired/revoked refresh token) it clears auth and bounces to login.
//
// Registered AFTER ErrorInterceptor so it sees the 401 first (Angular runs
// response handlers bottom-up through the provider array) and can refresh before
// ErrorInterceptor would redirect to /auth/login.
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { CommonCommunicationService } from '../services/commonCommunication.service';

// Auth/credential endpoints must NOT trigger a refresh-retry: they legitimately
// return 401 (wrong OTP/password) and the refresh endpoint is the recovery path.
const SKIP_REFRESH_ENDPOINTS = [
  '/v1/auth/refresh',
  '/v1/auth/logout',
  '/team/verify-otp',
  '/team/send-otp',
  '/team/verifySmsOtp',
  '/team/send-sms-otp',
  '/login',
  '/register',
  '/generate-forgot-password-link'
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private ccs: CommonCommunicationService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth for third-party URLs
    if (!req.url.startsWith(environment.apiBaseUrl)) {
      return next.handle(req);
    }

    // Skip auth/credential endpoints (their 401 is expected, not "expired").
    if (this.isSkippedEndpoint(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  // Refresh the token (once), queueing other 401s until it resolves.
  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.refreshAccessToken()).pipe(
        switchMap((newToken: string | null) => {
          this.isRefreshing = false;
          if (!newToken) {
            this.forceLogout();
            return throwError(() => new Error('Session expired'));
          }
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(req, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.forceLogout();
          return throwError(() => err);
        })
      );
    }

    // A refresh is already in flight — wait for it, then retry with the new token.
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addToken(req, token!)))
    );
  }

  // Exchange the refresh token for a new access token. Uses fetch (not HttpClient)
  // to bypass the interceptor chain entirely — no recursion, no expired token
  // attached. Returns the new access token, or null if refresh is not possible.
  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.ccs.getRefreshToken();
    if (!refreshToken) {
      return null;
    }
    try {
      const res = await fetch(`${environment.userService}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      if (!data || !data.token) {
        return null;
      }
      this.ccs.setAuthToken(data.token);
      localStorage.setItem('token', JSON.stringify(data.token));
      if (data.refreshToken) {
        this.ccs.setRefreshToken(data.refreshToken);
      }
      return data.token;
    } catch {
      return null;
    }
  }

  private forceLogout(): void {
    this.ccs.clearAuth();
    this.router.navigate(['/auth/login']).catch(() => {});
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { 'X-User-Access-Token': token }
    });
  }

  private isSkippedEndpoint(url: string): boolean {
    return SKIP_REFRESH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
  }
}
